import {
  getFirestore,
  collection,
  doc,
  getDocs,
  setDoc,
  query,
  where,
} from "firebase/firestore";
import firebaseApp from "@/lib/firebase";
import {
  ProgressAndLastSeenEntry,
  ProgressEntryUpdate,
} from "@/types/progressAndLastSeen";

/**
 * Repository class for progress collection operations
 * Handles CRUD operations for progress in Firestore
 */

export class ProgressRepository {
  private db = getFirestore(firebaseApp);
  private collectionName = "progress" as const;

  // Sets or updates the progress for a contentBlock for a user and course
  async setProgress({
    userId,
    courseId,
    contentBlockId,
    currentTime,
    seen,
  }: {
    userId: string;
    courseId: string;
    contentBlockId: string;
    currentTime?: number;
    seen?: boolean;
  }): Promise<void> {
    try {
      // Document path: progress/{userId}/{courseId}/{contentBlockId}
      const id = `${userId}_${courseId}_${contentBlockId}`;
      const progressEntry: ProgressEntryUpdate = {
        id,
        userId,
        courseId,
        contentBlockId,
        ...(currentTime !== undefined && { currentTime }),
        ...(seen !== undefined && { seen }),
      };

      const docRef = doc(this.db, this.collectionName, id);
      setDoc(docRef, { ...progressEntry });
    } catch (e) {
      console.error("Error setting course progress:", e);
    }
  }

  // Gets all episode progress values for a given course for a user.
  async getCourseProgress(
    userId: string,
    courseId: string
  ): Promise<ProgressAndLastSeenEntry[] | []> {
    try {
      const progressCol = collection(this.db, this.collectionName);
      const queryRef = query(
        progressCol,
        where("courseId", "==", courseId),
        where("userId", "==", userId)
      );

      const docsSnap = await getDocs(queryRef);
      const docsArray = docsSnap.docs.map((doc) =>
        doc.data()
      ) as ProgressAndLastSeenEntry[];
      return docsArray;
    } catch (e) {
      console.error("Error getting course progress:", e);
      return [];
    }
  }
}

// Export singleton instance
export const progressRepository = new ProgressRepository();
